import { type ImportJobDTO, type ImportProbeDTO, type ImportUploadSettings, MAX_IMPORT_BYTES, MAX_IMPORT_ROWS } from '@hearth/shared';
import { api, ApiError, errorMessage } from '../../../lib/api';
import type { ImportOptions } from '../ImportOptions';
import type { ScanCandidate } from './folderScan';

/** The calls a folder scan makes (a seam for tests). */
export type ScanApi = {
  probe: (file: File, signal?: AbortSignal) => Promise<ImportProbeDTO>;
  upload: (file: File, batchId: string, settings: ImportUploadSettings) => Promise<ImportJobDTO>;
};

export const scanApi: ScanApi = {
  probe: (file, signal) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return api.post<ImportProbeDTO>('/api/imports/probe', form, { signal });
  },
  upload: (file, batchId, settings) => {
    const form = new FormData();
    form.append('batchId', batchId);
    form.append('settings', JSON.stringify(settings));
    form.append('file', file, file.name);
    return api.post<ImportJobDTO>('/api/imports', form);
  },
};

/** What checking a file found, by path. */
export type ProbeOutcome =
  | { kind: 'read'; probe: ImportProbeDTO }
  /** 422: the importer cannot read it (scanned PDF, password-protected…); an upload would be refused the same way. */
  | { kind: 'unreadable'; message: string }
  /** Not checked this time (connection, server, file open elsewhere): worth another try. */
  | { kind: 'failed'; message: string };

/** Why an upload of this file would be refused, or null when it can be imported. */
export function importBlocker(candidate: ScanCandidate, outcome: ProbeOutcome | undefined): string | null {
  if (candidate.tooLarge) return `Larger than ${MAX_IMPORT_BYTES / 1024 / 1024} MB — split it into smaller files`;
  if (outcome?.kind === 'unreadable') return outcome.message;
  if (outcome?.kind === 'read' && outcome.probe.truncated) return `More than ${MAX_IMPORT_ROWS.toLocaleString()} rows — split it into smaller files`;
  if (outcome?.kind === 'read' && outcome.probe.totalRows === 0) return 'Only column names, no rows';
  return null;
}

/** Ticked after a check: the file holds at least one good or weak lead and can be imported as it is. */
export function worthImporting(outcome: ProbeOutcome | undefined): boolean {
  return outcome?.kind === 'read' && !outcome.probe.truncated && outcome.probe.leads.good + outcome.probe.leads.weak > 0;
}

/** Most good leads first, then most weak ones; files not read (yet) follow in path order. */
export function rankCandidates(candidates: readonly ScanCandidate[], outcomes: ReadonlyMap<string, ProbeOutcome>): ScanCandidate[] {
  const leads = (candidate: ScanCandidate) => {
    const outcome = outcomes.get(candidate.path);
    return outcome?.kind === 'read' ? outcome.probe.leads : null;
  };
  return [...candidates].sort((a, b) => {
    const left = leads(a);
    const right = leads(b);
    if (left && right && (left.good !== right.good || left.weak !== right.weak)) return right.good - left.good || right.weak - left.weak;
    if (left && !right) return -1;
    if (right && !left) return 1;
    return a.path.localeCompare(b.path, undefined, { numeric: true, sensitivity: 'base' });
  });
}

/** Pause after a rate-limit answer (the server counts probes per minute), and how many tries a file gets. */
const RATE_LIMIT_PAUSE_MS = 20_000;
const RATE_LIMIT_ATTEMPTS = 5;

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  const { promise, resolve } = Promise.withResolvers<void>();
  const done = () => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', done);
    resolve();
  };
  const timer = setTimeout(done, ms);
  signal?.addEventListener('abort', done, { once: true });
  return promise;
}

export type CheckOptions = {
  concurrency?: number;
  signal?: AbortSignal;
  onStart?: (path: string) => void;
  onResult: (path: string, outcome: ProbeOutcome) => void;
  /** Called with true when a check waits out the server's rate limit, false when it goes on. */
  onPause?: (paused: boolean) => void;
  wait?: (ms: number, signal?: AbortSignal) => Promise<void>;
};

/**
 * Sends each file to the probe, `concurrency` at a time (the server reads every row). A rate-limit answer pauses and
 * tries the same file again. Cancelling stops new checks and drops the ones in flight (no outcome reported for them).
 */
export async function checkFiles(candidates: readonly ScanCandidate[], probe: ScanApi['probe'], options: CheckOptions): Promise<void> {
  const { concurrency = 2, signal, onStart, onResult, onPause, wait = sleep } = options;
  let next = 0;
  const checkOne = async (candidate: ScanCandidate): Promise<ProbeOutcome | null> => {
    let file: File;
    try {
      file = await candidate.file();
    } catch {
      return { kind: 'failed', message: 'This computer could not open the file (moved, deleted or open in another program).' };
    }
    for (let attempt = 1; ; attempt++) {
      try {
        return { kind: 'read', probe: await probe(file, signal) };
      } catch (error) {
        if (signal?.aborted) return null;
        if (error instanceof ApiError && error.status === 429 && attempt < RATE_LIMIT_ATTEMPTS) {
          onPause?.(true);
          await wait(RATE_LIMIT_PAUSE_MS * attempt, signal);
          onPause?.(false);
          if (signal?.aborted) return null;
          continue;
        }
        if (error instanceof ApiError && error.status === 422) return { kind: 'unreadable', message: error.message };
        return { kind: 'failed', message: errorMessage(error) };
      }
    }
  };
  const worker = async () => {
    while (next < candidates.length && !signal?.aborted) {
      const candidate = candidates[next++]!;
      onStart?.(candidate.path);
      const outcome = await checkOne(candidate);
      if (outcome) onResult(candidate.path, outcome);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, candidates.length) }, worker));
}

/** Progress of one file through "Import N files". */
export type ImportStep =
  | { state: 'uploading' }
  /** Uploaded with the shared settings; the server is now checking its rows and importing the ready ones. */
  | { state: 'done'; jobId: string }
  /** Uploaded, but no column was taken for the business name: the columns are matched on the import's own page. */
  | { state: 'needs-matching'; jobId: string; message: string }
  | { state: 'failed'; message: string };

/**
 * One import per file, one file at a time, all sharing `batchId`: each upload carries the shared settings and the server
 * imports it straight away with its guessed column matching. Each file keeps the server's default lead source.
 * Cancelling stops before the next file. Resolves with the number of imports created.
 */
export async function importFiles(
  candidates: readonly ScanCandidate[],
  batchId: string,
  options: Omit<ImportOptions, 'defaultSourceId'>,
  upload: ScanApi['upload'],
  { signal, onStep }: { signal?: AbortSignal; onStep: (path: string, step: ImportStep) => void },
): Promise<number> {
  const settings: ImportUploadSettings = {
    defaultOwnerId: options.defaultOwnerId,
    defaultTagIds: options.defaultTagIds,
    defaultCountry: options.defaultCountry,
    duplicatePolicy: options.duplicatePolicy,
    defaultConsentBasis: options.defaultConsentBasis,
    businessKind: options.businessKind,
  };
  let created = 0;
  for (const candidate of candidates) {
    if (signal?.aborted) break;
    const { path } = candidate;
    onStep(path, { state: 'uploading' });
    let job: ImportJobDTO;
    try {
      job = await upload(await candidate.file(), batchId, settings);
    } catch (error) {
      onStep(path, { state: 'failed', message: errorMessage(error) });
      continue;
    }
    created++;
    // 'uploaded': the server found no business-name column, so it waits for the columns to be matched by hand.
    onStep(path, job.status === 'uploaded' ? { state: 'needs-matching', jobId: job.id, message: 'No column was recognised as the business name.' } : { state: 'done', jobId: job.id });
  }
  return created;
}

/** crypto.randomUUID exists only in a secure context (https or localhost); getRandomValues works everywhere. */
export function newBatchId(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export type BatchSummary = {
  files: number;
  /** Files imported with possible duplicates still waiting for a decision. */
  needsReview: number;
  /** Possible duplicates still waiting for a decision, over every imported file. */
  undecided: number;
  /** Files waiting for their columns to be matched (nothing imported yet). */
  needsMatching: number;
  /** Files whose rows are being checked or imported right now. */
  running: number;
  /** Files that went in (with or without decisions left): they have leads. */
  imported: number;
};

export function batchSummary(jobs: readonly ImportJobDTO[]): BatchSummary {
  const count = (test: (job: ImportJobDTO) => boolean) => jobs.filter(test).length;
  return {
    files: jobs.length,
    needsReview: count((job) => job.status === 'validated'),
    undecided: jobs.reduce((sum, job) => sum + (job.status === 'validated' ? job.unresolvedDuplicates : 0), 0),
    needsMatching: count((job) => job.status === 'uploaded'),
    running: count((job) => job.status === 'validating' || job.status === 'importing'),
    imported: count((job) => job.status === 'validated' || job.status === 'completed'),
  };
}
